import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../../lib/api/apiService";
import { LogoutOutlined } from '@ant-design/icons';
import { Dropdown, Space, Drawer } from 'antd';
import { useIsFetching } from '@tanstack/react-query';
import useCurrentUser from "../../hooks/useCurrentUser";
import { useCart } from "../../context/CartContext";
import { getCartQueryKey } from "../../hooks/useCartQuery";
import { getImageUrl } from "../../utils/apiHelpers";
import {
  extractProductThumbnail,
  resolveCartLineUnitPrice,
  parseStock,
} from "../../utils/productHelpers";
import { PRODUCT_PLACEHOLDER_IMAGE } from "../../utils/constants";
import useCartOperations from "../../hooks/useCartOperations";
import { useTranslation } from "react-i18next";
import SearchAutocomplete from "./SearchAutocomplete";
import { buildCategoriesSearchUrl } from "../../lib/search/searchRoutes";


const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuToggleRef = useRef(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: user } = useCurrentUser({ forceRefresh: false });
  const {
    items: cartItems,
    totalItems: cartCount,
    updateQuantity,
    removeItem,
    refetchCart,
  } = useCart();
  const cachedUser = authService.getCachedUser();
  const userId = user?._id || user?.id || cachedUser?._id || cachedUser?.id;
  const { removeItemFromAPI, updateItemQuantityInAPI } = useCartOperations();
  const { t, i18n } = useTranslation();

  // Synchroniser le champ avec ?search= sur /categories
  useEffect(() => {
    if (location.pathname === "/categories") {
      setSearchTerm(searchParams.get("search") || "");
    } else {
      setSearchTerm("");
    }
  }, [location.pathname, searchParams]);

  const handleSearchSubmit = useCallback(
    (event) => {
      event?.preventDefault?.();
      const query = searchTerm.trim();
      navigate(buildCategoriesSearchUrl(query));
      setIsMenuOpen(false);
    },
    [navigate, searchTerm],
  );

  const cartFetchInFlight =
    useIsFetching({ queryKey: getCartQueryKey(userId) }) > 0;
  const displayItems = cartItems;
  const isLoadingCart =
    isCartDrawerOpen &&
    isAuthenticated &&
    !!userId &&
    cartFetchInFlight &&
    cartItems.length === 0;

  // Total panier : prix ligne si valide, sinon prix effectif produit (promo ignorée si null ou 0)
  const calculatedTotal = useMemo(() => {
    if (displayItems.length === 0) return 0;
    return displayItems.reduce((sum, item) => {
      const price = resolveCartLineUnitPrice(item);
      const quantity = item.quantity || 1;
      return sum + price * quantity;
    }, 0);
  }, [displayItems]);
  useEffect(() => {
    // Vérifier l'état d'authentification au chargement et lors des changements
    setIsAuthenticated(authService.isAuthenticated());
  }, [location]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (
        mobileMenuRef.current?.contains(target) ||
        mobileMenuToggleRef.current?.contains(target)
      ) {
        return;
      }
      setIsMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleLogout = useCallback(async () => {
    await authService.logout();
    setIsAuthenticated(false);
    navigate('/');
    window.location.reload(); // Rafraîchir pour mettre à jour l'état
  }, [navigate]);

  const handleCartDrawerClose = () => {
    setIsCartDrawerOpen(false);
  };

  const handleCartButtonClick = (e) => {
    e.preventDefault();
    setIsCartDrawerOpen(true);
    if (isAuthenticated && userId) {
      refetchCart();
    }
  };

  const handleNavigateFromCartDrawer = () => {
    handleCartDrawerClose();
    if (isAuthenticated && userId) {
      refetchCart();
    }
  };

  // Fonction pour gérer la suppression d'un item avec appel API
  const handleRemoveItem = (item) => {
    if (item._id || item.id) {
      const itemId = item._id || item.id;
      removeItemFromAPI(
        itemId,
        () => {
          // Succès : supprimer du panier local aussi
          if (item.productId) {
            removeItem(item.productId, item.variantId || null, item.optionKey || null);
          }
        },
        (error) => {
          console.error('Erreur lors de la suppression:', error);
        }
      );
    } else if (item.productId) {
      // Si pas d'itemId, supprimer directement du panier local
      removeItem(item.productId, item.variantId || null, item.optionKey || null);
    }
  };

  // Fonction pour gérer la mise à jour de quantité avec appel API
  const handleUpdateQuantity = (item, newQuantity) => {
    let validQuantity = Math.max(1, parseInt(newQuantity, 10) || 1);

    // Limiter au stock disponible (0 = rupture, valeur valide ; null = inconnu).
    const stock = parseStock(item.stock);
    if (stock === 0) {
      return; // produit indisponible : pas de mise à jour de quantité
    }
    if (stock !== null && validQuantity > stock) {
      validQuantity = stock;
    }

    if (item._id || item.id) {
      const itemId = item._id || item.id;
      updateItemQuantityInAPI(
        itemId,
        validQuantity,
        () => {
          // Succès : mettre à jour dans le panier local aussi
          if (item.productId) {
            updateQuantity(item.productId, item.variantId || null, item.optionKey || null, validQuantity);
          }
        },
        (error) => {
          console.error('Erreur lors de la mise à jour:', error);
        }
      );
    } else if (item.productId) {
      // Si pas d'itemId, mettre à jour directement dans le panier local
      updateQuantity(item.productId, item.variantId || null, item.optionKey || null, validQuantity);
    }
  };

  const menuItems = useMemo(() => {
    if (!user) {
      return [];
    }

    const headerLabel =
      user.email ||
      user.phone ||
      [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
      t('navbar.my_account');

    const items = [
      {
        key: '1',
        label: headerLabel,
        disabled: true,
      },
      {
        type: 'divider',
      },
    ];

    if (user.role === 'SELLER') {
      items.push({
        key: '3',
        label: t('navbar.dashboard'),
        onClick: () => navigate('/dashboard'),
        extra: '⌘T',
      });
    } else if (user.role === 'ADMIN') {
      items.push({
        key: '3',
        label: t('navbar.dashboard'),
        onClick: () => navigate('/admin-dashboard'),
        extra: '⌘T',
      });
    } else if (user.role === 'CLIENT') {
      items.push({
        key: '3',
        label: t('navbar.account_page'),
        onClick: () => navigate('/account'),
      });
    }

    items.push({
      key: '4',
      label: t('navbar.logout'),
      onClick: () => handleLogout(),
      extra: '⌘D',
      icon: <LogoutOutlined />,
      className: 'text-red-500 hover:text-red-600 hover:cursor-pointer',
    });

    return items;
  }, [user, navigate, handleLogout, t]);

  return (
    <nav ref={navRef} className="bg-white sticky top-0 z-[100] px-4 lg:px-0">
      <div className="md:mx-8 lg:mx-16 mx-auto">
        {/* Barre supérieure */}
        <div className="bg-white border-b border-gray-100 py-3">
          <div className="mx-auto">
            <div className="flex items-center justify-between h-16">
              {/* Logo et livraison */}
              <div className="flex items-center space-x-4">
                <button onClick={() => navigate('/')} className="">
                  <img className="w-42 h-12 cursor-pointer" src="/images/logo.png" alt="logo" />
                </button>
                {/* Temporairement masqué
                <div className="hidden lg:block border-l-2 border-gray-400 pl-4">
                  <span className="text-sm text-gray-500">{t("navbar.delivery_to")} <b>Togo</b></span>
                </div>
                */}
              </div>

              {/* Barre de recherche */}
              <SearchAutocomplete
                navRef={navRef}
                value={searchTerm}
                onChange={setSearchTerm}
                onSubmit={handleSearchSubmit}
                className="hidden lg:flex flex-1 max-w-xl xl:max-w-md mx-8"
                inputClassName="w-full pl-4 pr-12 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-light focus:border-transparent"
              />

              {/* Utilitaires utilisateur */}
              <div className="flex items-center space-x-4">
                {/* Sélecteur de langue */}
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'fr',
                        label: (
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">Français</span>
                            <span className="text-xs text-gray-500">FR</span>
                          </div>
                        ),
                        onClick: () => i18n.changeLanguage("fr"),
                        className: i18n.language === 'fr' ? 'bg-orange-50' : '',
                      },
                      {
                        key: 'en',
                        label: (
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">English</span>
                            <span className="text-xs text-gray-500">EN</span>
                          </div>
                        ),
                        onClick: () => i18n.changeLanguage("en"),
                        className: i18n.language === 'en' ? 'bg-orange-50' : '',
                      },
                    ],
                  }}
                  placement="bottomRight"
                >
                  <button
                    onClick={(e) => e.preventDefault()}
                    className="hidden md:flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 font-medium"
                  >
                    <img className="w-5 h-5" src="/svg/globe.svg" alt="globe" />
                    <span>{i18n.language === 'fr' ? 'Français' : 'English'}</span>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </Dropdown>

                {/* Bouton panier : visible pour tous (invités et connectés) */}
                <button
                  onClick={handleCartButtonClick}
                  className="p-2 hover:bg-gray-100 rounded-full relative"
                  aria-label={t("common.cart")}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="21.283" height="21.283" viewBox="0 0 21.283 21.283">
                    <g id="Groupe_8601" data-name="Groupe 8601" transform="translate(0.75 0.75)">
                      <path id="Tracé_13756" data-name="Tracé 13756" d="M1148.968,58.484A1.484,1.484,0,1,1,1147.484,57,1.483,1.483,0,0,1,1148.968,58.484Z" transform="translate(-1140.064 -40.184)" fill="none" stroke="#141c1d" strokeWidth="1.5" />
                      <path id="Tracé_13757" data-name="Tracé 13757" d="M1157.968,58.484A1.484,1.484,0,1,1,1156.484,57,1.483,1.483,0,0,1,1157.968,58.484Z" transform="translate(-1140.162 -40.184)" fill="none" stroke="#141c1d" strokeWidth="1.5" />
                      <path id="Tracé_13758" data-name="Tracé 13758" d="M1143.957,41.978h11.87a3.956,3.956,0,0,1,3.957,3.957v4.946a3.956,3.956,0,0,1-3.957,3.957h-7.913a3.956,3.956,0,0,1-3.956-3.957Zm0,0A1.981,1.981,0,0,0,1141.978,40H1140m3.957,5.935h15.332" transform="translate(-1140 -40)" fill="none" stroke="#141c1d" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                    </g>
                  </svg>
                  {cartCount > 0 && (
                    <span className="absolute -top-0 -right-0 bg-orange-light text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </button>

                {isAuthenticated ? (
                  <>
                    <Link
                      to="/account?tab=favorites"
                      className="p-2 hover:bg-gray-100 rounded-full relative"
                      aria-label={t("navbar.favorites", "Mes favoris")}
                    >
                      <img className="h-5 w-5" src="/svg/love.svg" alt="love" />
                    </Link>

                    {user?.role === 'SELLER' ||
                    user?.role === 'ADMIN' ||
                    user?.role === 'CLIENT' ? (
                      <Dropdown menu={{ items: menuItems }}>
                        <span
                          onClick={(e) => e.preventDefault()}
                          className="p-2 hover:bg-gray-50 rounded-full hover:cursor-pointer"
                          role="button"
                          tabIndex={0}
                          aria-label={t('navbar.my_account')}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.currentTarget.click();
                            }
                          }}
                        >
                          <Space>
                            <img className="w-5 h-5" src="/svg/nav-user.svg" alt="" />
                          </Space>
                        </span>
                      </Dropdown>
                    ) : (
                      <Link to="/account" className="p-2 hover:bg-gray-100 rounded-full">
                        <img className="w-5 h-5" src="/svg/nav-user.svg" alt="user" />
                      </Link>
                    )}
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="bg-orange-hover text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-orange-light transition-colors hidden md:inline-block"
                  >
                    {t("navbar.login")}
                  </Link>
                )}

                {/* Menu mobile */}
                <button
                  ref={mobileMenuToggleRef}
                  type="button"
                  onClick={() => setIsMenuOpen((open) => !open)}
                  className="md:hidden p-2 hover:bg-gray-100 rounded-full"
                  aria-expanded={isMenuOpen}
                  aria-label={isMenuOpen ? t("navbar.close_menu") : t("navbar.open_menu")}
                >
                  {isMenuOpen ? (
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Barre de navigation inférieure (desktop uniquement — sur mobile les liens sont dans le menu hamburger) */}
      <div className="hidden md:block bg-gray-100">
        <div className="md:mx-8 lg:mx-16 mx-auto">
          <div className="flex items-center justify-between h-14">
            {/* Navigation principale */}
            <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
              <Link
                to="/"
                className={`transition-colors hover:underline ${location.pathname === "/"
                  ? "text-red-600 font-medium"
                  : "text-gray-700 hover:text-gray-900"
                  }`}
              >
                {t("navbar.home")}
              </Link>

              <Link
                to="/categories"
                className={`transition-colors hover:underline ${location.pathname === "/categories"
                  ? "text-orange-hover font-medium"
                  : "text-gray-700 hover:text-gray-900"
                  }`}
              >
                <span>{t("navbar.products")}</span>
              </Link>
              <Link
                to="/promotions"
                className={`transition-colors hover:underline ${location.pathname === "/promotions"
                  ? "text-orange-light font-medium"
                  : "text-gray-700 hover:text-gray-900"
                  }`}
              >
                {t("navbar.promotions")}
              </Link>
              <Link
                to="/faq"
                className={`transition-colors hover:underline ${location.pathname === "/faq"
                  ? "text-orange-light font-medium"
                  : "text-gray-700 hover:text-gray-900"
                  }`}
              >
                {t("navbar.help_faq")}
              </Link>
              <Link
                to="/become-seller"
                className={`transition-colors hover:underline ${location.pathname === "/become-seller"
                  ? "text-orange-light font-medium"
                  : "text-gray-700 hover:text-gray-900"
                  }`}
              >
                {t("navbar.become_seller")}
              </Link>
              <Link
                to="/boutiques"
                className={`transition-colors hover:underline ${location.pathname === "/boutiques"
                  ? "text-orange-light font-medium"
                  : "text-gray-700 hover:text-gray-900"
                  }`}
              >
                <span>{t("navbar.shops")}</span>
              </Link>
            </div>

            {/* Temporairement masqué — Offre du jour
            <div className="hidden md:flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors cursor-pointer font-medium">
              <span>{t("navbar.daily_offer")}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            */}
          </div>
        </div>
      </div>

      {/* Menu mobile */}
      {isMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden bg-white border-t border-gray-200 shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100">
            <SearchAutocomplete
              navRef={navRef}
              value={searchTerm}
              onChange={setSearchTerm}
              onSubmit={handleSearchSubmit}
              inputClassName="w-full pl-4 pr-10 py-2 text-gray-600 border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-orange-light focus:border-transparent"
              showIcon={false}
            />
          </div>
          <div className="px-4 py-2 space-y-2 border-t border-gray-100">
            <Link
              to="/"
              className={`block py-2 ${location.pathname === "/" ? "text-red-800 font-medium" : "text-gray-700 hover:text-gray-900"}`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t("navbar.home")}
            </Link>
            <Link
              to="/categories"
              className={`block py-2 ${location.pathname === "/categories" ? "text-orange-light font-medium" : "text-gray-700 hover:text-gray-900"}`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t("navbar.products")}
            </Link>
            <Link
              to="/promotions"
              className={`block py-2 ${location.pathname === "/promotions" ? "text-orange-light font-medium" : "text-gray-700 hover:text-gray-900"}`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t("navbar.promotions")}
            </Link>
            <Link
              to="/faq"
              className={`block py-2 ${location.pathname === "/faq" ? "text-orange-light font-medium" : "text-gray-700 hover:text-gray-900"}`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t("navbar.help_faq")}
            </Link>
            <Link
              to="/become-seller"
              className={`block py-2 ${location.pathname === "/become-seller" ? "text-orange-light font-medium" : "text-gray-700 hover:text-gray-900"}`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t("navbar.become_seller")}
            </Link>
            <Link
              to="/boutiques"
              className={`block py-2 ${location.pathname === "/boutiques" ? "text-orange-light font-medium" : "text-gray-700 hover:text-gray-900"}`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t("navbar.shops")}
            </Link>
            {isAuthenticated ? (
              <button
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
                className="flex items-center space-x-2 w-full text-left py-2 px-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>{t("navbar.logout")}</span>
              </button>
            ) : (
              <Link
                to="/login"
                className="block py-2 text-gray-700 hover:text-gray-900"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("navbar.login")}
              </Link>
            )}
            {/* Sélecteur de langue mobile */}
            <div className="border-t border-gray-200 pt-2 mt-2">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-2">
                  <img className="w-5 h-5" src="/svg/globe.svg" alt="globe" />
                  <span className="text-sm font-medium text-gray-700">{t("navbar.language")}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      i18n.changeLanguage("fr");
                      setIsMenuOpen(false);
                    }}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      i18n.language === 'fr'
                        ? 'bg-orange-hover text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    FR
                  </button>
                  <button
                    onClick={() => {
                      i18n.changeLanguage("en");
                      setIsMenuOpen(false);
                    }}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      i18n.language === 'en'
                        ? 'bg-orange-hover text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    EN
                  </button>
                </div>
              </div>
            </div>
            {/* Temporairement masqué
            <div className="block py-2 text-gray-700">
              {t("navbar.daily_offer")}
            </div>
            */}
          </div>
        </div>
      )}

      {/* Drawer du panier */}
      <Drawer
        title={t("common.cart")}
        closable={{ 'aria-label': 'Close Button' }}
        onClose={handleCartDrawerClose}
        open={isCartDrawerOpen}
        placement="right"
        width={420}
      >
        {isLoadingCart ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-hover mb-4"></div>
            <p className="text-gray-500">{t("navbar.cart_loading")}</p>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <p className="text-gray-500 text-lg mb-2">{t("navbar.cart_empty")}</p>
            <p className="text-gray-400 text-sm">{t("navbar.cart_empty_description")}</p>
            <Link
              to="/categories"
              onClick={handleCartDrawerClose}
              className="mt-4 bg-orange-hover text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-orange-light transition-colors"
            >
              {t("navbar.view_products")}
            </Link>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            {/* Liste des articles */}
            <div className="flex-1 overflow-y-auto space-y-4 mb-4">
              {displayItems.map((item) => {
                const itemId = item.id;
                const itemKey = itemId;
                const product = item.product || {};
                // Pour les invités : name/price/image sont sur l'item (panier local) ;
                // pour les connectés : sur item.product (jointure côté API).
                const productName = item.name || product.name || 'Produit';
                const price = resolveCartLineUnitPrice(item);
                const quantity = item.quantity || 1;
                const itemTotal = price * quantity;
                // Stock dispo (0 = rupture, valeur valide ; null = inconnu) ; fallback product.quantity.
                const itemStock = parseStock(item.stock) ?? parseStock(product.quantity);
                const atMaxStock =
                  itemStock !== null && itemStock > 0 && quantity >= itemStock;
                // Produit en rupture / indisponible (statut outofstock ou stock = 0).
                const isUnavailable =
                  item.isUnavailable === true ||
                  item.status === 'outofstock' ||
                  product?.status === 'outofstock' ||
                  itemStock === 0;

                // Récupérer l'image (priorité : item local puis product)
                const imageUrl = item.image
                  ? extractProductThumbnail({ image: item.image })
                  : extractProductThumbnail(product);

                // Récupérer le fournisseur : item.shopName (invités / local) ou product.shop.shop_name (API)
                const shop = product.shop || {};
                const supplierName = (item.shopName && String(item.shopName).trim()) || shop.shop_name || '';

                return (
                  <div key={itemKey} className="flex flex-col gap-4 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-start justify-start gap-4">
                      <img
                        src={imageUrl}
                        alt={productName}
                        className={`w-14 h-14 object-cover rounded-lg flex-shrink-0 ${isUnavailable ? 'grayscale opacity-70' : ''}`}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.src = PRODUCT_PLACEHOLDER_IMAGE;
                        }}
                      />
                      <h4 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2">
                        {productName}
                      </h4>
                    </div>


                    <div className="flex-1 min-w-0">
                      {/* Contrôle de quantité */}
                      <div className="flex items-center justify-between gap-3 mb-4">
                        {isUnavailable ? (
                          <span className="inline-block px-2 py-0.5 text-[10px] leading-tight font-medium rounded-full bg-red-100 text-red-700 whitespace-nowrap">
                            Ce produit n'est plus disponible
                          </span>
                        ) : (
                        <div className="flex items-center border border-gray-300 rounded-full text-sm">
                          <button
                            onClick={() => handleUpdateQuantity(item, quantity - 1)}
                            className="px-2 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-l-full transition-colors"
                            disabled={quantity <= 1}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          <input
                            type="number"
                            value={quantity}
                            onChange={(e) => {
                              const newQty = parseInt(e.target.value, 10) || 1;
                              handleUpdateQuantity(item, newQty);
                            }}
                            className="ml-1.5 w-14 px-2 py-1 text-center text-sm focus:outline-none focus:ring-0"
                            min="1"
                            max={itemStock ?? undefined}
                          />
                          <button
                            onClick={() => handleUpdateQuantity(item, quantity + 1)}
                            disabled={atMaxStock}
                            className="px-2 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-r-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                        )}

                        {/* Prix en orange */}
                        <p className="text-orange-hover font-semibold text-base">
                          {itemTotal.toLocaleString()} Fcfa
                        </p>

                        {/* Bouton Supprimer */}
                        <button
                          onClick={() => handleRemoveItem(item)}
                          className="bg-red-50 border border-red-500 whitespace-now nowrap flex items-center gap-1 rounded-full px-2 py-1 text-red-500 hover:text-red-600 transition-colors text-xs font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {t("navbar.remove")}
                        </button>
                      </div>

                      {!isUnavailable && atMaxStock && (
                        <p className="text-xs text-amber-600 font-medium mb-2">
                          Quantité maximale atteinte : {itemStock} disponible{itemStock > 1 ? 's' : ''}
                        </p>
                      )}

                      {/* Fournisseur */}
                      <p className="text-xs mb-3">
                        <b>{t("navbar.supplier")} : </b> {supplierName || '—'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total et boutons */}
            <div className="space-y-3 mt-auto">
              <div className="flex justify-between items-center">
                <span className="text-base font-medium text-gray-900">{t("navbar.total")}:</span>
                <span className="text-lg font-bold text-gray-900">
                  {calculatedTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} FCFA
                </span>
              </div>

              <Link
                to="/cart"
                onClick={handleNavigateFromCartDrawer}
                className="block w-full bg-orange-hover text-white text-center py-3 rounded-full font-medium hover:bg-orange-light hover:text-white"
              >
                {t("navbar.view_cart")}
              </Link>

              <Link
                to="/checkout"
                onClick={handleNavigateFromCartDrawer}
                className="block w-full border-2 border-orange-hover text-orange-hover text-center py-3 rounded-full font-medium hover:bg-orange-50 hover:text-orange-hover"
              >
                {t("navbar.checkout_button")}
              </Link>
            </div>
          </div>
        )}
      </Drawer>
    </nav>
  );
};

export default Navbar; 